import { useFrame } from '@react-three/fiber';
import React, { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { type Group, Vector3 } from 'three';

import type { GroupProps } from 'types/r3f';
import { createNoise2D } from 'utils/noise';

import { BushfireQuadCloud } from './BushfireQuadCloud/BushfireQuadCloud';

const CLOUD_INTRO_SPEED = 0.4;

const noise = createNoise2D();

type CloudHandle = {
  position: Vector3;
  scale: number;
  opacity: number;
  userData: {
    startPosition: Vector3;
    finalPosition: Vector3;
    volume: number;
    progress: number;
    hasInitialized: boolean;
  };
  isLoaded: boolean;
};

export interface BushfireCloudsProps extends GroupProps {
  cloudsRadius: number;
  holeRadius: number;
  lightningAreaRadius: number;
  altitude: number;
  density?: number;
  baseCloudVolume?: number;
  verticalSpread?: number;
}

const BushfireClouds = forwardRef<Group, BushfireCloudsProps>(
  (
    {
      cloudsRadius,
      holeRadius,
      altitude,
      density = 0.05,
      baseCloudVolume = 18.5,
      verticalSpread = 12,
      ...props
    },
    ref
  ) => {
    const groupRef = useRef<Group>(null!);
    const cloudsRef = useRef<CloudHandle[]>([]);

    useImperativeHandle(ref, () => groupRef.current);

    const clouds = useMemo(() => {
      cloudsRef.current.length = 0;
      const acc = [];
      let count = 0;

      const normalBounds = new Vector3(24, 8, 24);
      const largeBounds = new Vector3(48, 8, 48);

      for (let i = 0; i < cloudsRadius * 2 * density; i++) {
        for (let j = 0; j < cloudsRadius * 2 * density; j++) {
          const xFactor = i / (cloudsRadius * 2 * density - 1);
          const zFactor = j / (cloudsRadius * 2 * density - 1);

          const dx = xFactor - 0.5;
          const dz = zFactor - 0.5;
          const totalDistance = dx * dx + dz * dz;

          if (totalDistance > 0.25) continue;

          if (totalDistance > 0.2) {
            const chance = 1 - (totalDistance - 0.2) / 0.05;
            if (Math.random() > chance) continue;
          }

          const nx = xFactor * cloudsRadius * 2 * 0.05;
          const nz = zFactor * cloudsRadius * 2 * 0.05;
          const n = (noise(nx, nz) + 1) / 2;

          const x = dx * cloudsRadius * 2;
          const z = dz * cloudsRadius * 2;

          const radius = Math.sqrt(Math.pow(dx * 2, 2) + Math.pow(dz * 2, 2));

          let chance = radius * (cloudsRadius / holeRadius);
          chance = Math.max(0, Math.min(1, chance));
          chance = Math.pow(chance, 4);
          chance *= n;

          if (Math.random() > chance) continue;

          let bounds = normalBounds;
          let volume = baseCloudVolume;
          let y = altitude + (n - 0.5) * verticalSpread;

          const isLarge = Math.random() < 0.1;
          const isHuge = Math.random() < 0.05;

          if (isHuge) {
            bounds = largeBounds;
            volume *= 3;
            y += 30;
          } else if (isLarge) {
            bounds = largeBounds;
            volume *= 2;
            y += 10;
          }

          const finalPosition = new Vector3(x, y, z);

          const offset = new Vector3(
            Math.random() * 2 - 1,
            Math.random() * 2 - 1,
            Math.random() * 2 - 1
          ).multiplyScalar(volume * 0.6);

          const startPosition = finalPosition.clone().add(offset);

          const cloudIdx = count;

          const cloud = (
            <BushfireQuadCloud
              key={`${i}:${j}`}
              ref={ref => {
                if (ref) cloudsRef.current[cloudIdx] = ref as CloudHandle;
              }}
              position={startPosition.clone()}
              opacity={0}
              userData={{
                startPosition,
                finalPosition,
                volume,
                progress: 0,
                hasInitialized: false,
              }}
              segmentCount={6}
              bounds={bounds}
              volume={volume}
              baseLight={0.8}
            />
          );

          acc.push(cloud);
          count++;
        }
      }

      cloudsRef.current = Array(count).fill(null);

      return acc;
    }, [
      cloudsRadius,
      holeRadius,
      altitude,
      density,
      baseCloudVolume,
      verticalSpread,
    ]);

    useFrame((_, deltaTime) => {
      const animationSpeed = deltaTime * CLOUD_INTRO_SPEED;

      for (const cloud of cloudsRef.current) {
        if (!cloud || !cloud.isLoaded) continue;

        if (!cloud.userData.hasInitialized) {
          cloud.userData.progress += animationSpeed;

          if (cloud.userData.progress >= 1) {
            cloud.userData.progress = 1;
            cloud.userData.hasInitialized = true;
          }
        } else {
          cloud.userData.progress += animationSpeed;
        }

        const oscillationProgress =
          0.5 + 0.5 * Math.sin(cloud.userData.progress);

        cloud.position.lerpVectors(
          cloud.userData.startPosition,
          cloud.userData.finalPosition,
          oscillationProgress
        );

        cloud.opacity = Math.max(0.6, 0.5 + oscillationProgress) / 1.8;
      }
    });

    return (
      <group ref={groupRef} {...props}>
        {clouds}
      </group>
    );
  }
);

BushfireClouds.displayName = 'BushfireClouds';
export default BushfireClouds;
